import { Check, ShieldCheck, X } from 'lucide-react';

import { cn } from '@/lib/utils';
import { PASSWORD_RULES, passwordScore } from '../schemas/password.schema';

export interface PasswordStrengthProps {
  /** What is currently typed into the new-password field. */
  value: string;
  /**
   * What is typed into the confirm field, when there is one.
   *
   * Given it, "both match" joins the checklist — it is the rule people trip on
   * most and the only one they cannot see for themselves behind the dots.
   */
  confirmation?: string;
}

/**
 * The meter's six steps, one per point of `passwordScore`. Widths are named
 * classes rather than a percentage, because a runtime width would need an
 * inline style.
 */
const STEPS = [
  { width: 'w-0', bar: '', label: 'Nothing typed yet', tone: 'text-ink-3' },
  { width: 'w-1/6', bar: 'bg-rose-500', label: 'Very weak', tone: 'text-rose-600 dark:text-rose-400' },
  { width: 'w-2/6', bar: 'bg-rose-500', label: 'Weak', tone: 'text-rose-600 dark:text-rose-400' },
  { width: 'w-3/6', bar: 'bg-amber-500', label: 'Fair', tone: 'text-amber-600 dark:text-amber-400' },
  { width: 'w-4/6', bar: 'bg-amber-500', label: 'Getting there', tone: 'text-amber-600 dark:text-amber-400' },
  { width: 'w-5/6', bar: 'bg-blue-500', label: 'Good', tone: 'text-blue-600 dark:text-blue-400' },
  { width: 'w-full', bar: 'bg-emerald-500', label: 'Strong', tone: 'text-emerald-600 dark:text-emerald-400' },
];

/**
 * How far the password being typed has got, and what is still missing.
 *
 * Reads `PASSWORD_RULES` rather than listing the rules again, so a tick here can
 * never appear beside something the schema would still refuse (CLAUDE.md §3).
 *
 * `aria-live="polite"` on the summary: somebody who cannot see the bar fill
 * still hears "Good" as it changes, and hears it once rather than on every
 * keystroke, because only the summary line is live — not the whole list.
 */
export function PasswordStrength({ value, confirmation }: PasswordStrengthProps) {
  const score = passwordScore(value);
  const step = STEPS[Math.min(score, STEPS.length - 1)];

  const checks = [
    ...PASSWORD_RULES.map((rule) => ({
      key: rule.key,
      label: rule.label,
      done: rule.test(value),
    })),
    // Only when there is a confirmation field to be wrong about.
    ...(confirmation === undefined
      ? []
      : [
          {
            key: 'match',
            label: 'Both entries match',
            done: value.length > 0 && value === confirmation,
          },
        ]),
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between gap-2">
          <span className="text-[11px] font-black uppercase tracking-widest text-ink-3">
            Strength
          </span>
          <span aria-live="polite" className={cn('text-[12px] font-black', step.tone)}>
            {step.label}
          </span>
        </div>

        <div className="h-2 w-full overflow-hidden rounded-full bg-surface-3">
          <div
            className={cn('h-full rounded-full transition-all duration-500', step.width, step.bar)}
            aria-hidden
          />
        </div>
      </div>

      <ul className="flex flex-col gap-2">
        {checks.map((check) => (
          <li key={check.key} className="flex items-center gap-2.5">
            <span
              className={cn(
                'grid h-5 w-5 shrink-0 place-items-center rounded-full transition-colors duration-300',
                check.done
                  ? 'bg-emerald-100 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400'
                  : 'bg-surface-3 text-ink-3',
              )}
              aria-hidden
            >
              {check.done ? (
                <Check className="h-3 w-3" strokeWidth={3.5} />
              ) : (
                <X className="h-3 w-3" strokeWidth={3} />
              )}
            </span>
            <span
              className={cn(
                'text-[12.5px] font-bold transition-colors duration-300',
                check.done ? 'text-ink' : 'text-ink-3',
              )}
            >
              {check.label}
            </span>
          </li>
        ))}
      </ul>

      {score >= STEPS.length - 1 && (
        <p className="flex items-center gap-2 rounded-2xl bg-emerald-50 dark:bg-emerald-950/40 px-3 py-2.5 text-[11.5px] font-bold text-emerald-700 dark:text-emerald-300 animate-fade-up">
          <ShieldCheck className="h-4 w-4 shrink-0" aria-hidden />
          That is a strong password. Nothing here is ever shown to anyone else.
        </p>
      )}
    </div>
  );
}
